import { useState } from 'react'
import { Play, Heart, MessageCircle, Share2, Eye, ExternalLink } from 'lucide-react'
import { platformColors, platformIcons, platformNames } from '../data/config'
import { getTheme } from '../utils/colors'
import WorkDetailModal from './WorkDetailModal'

function Works({ config, isLight = false }) {
  const [filter, setFilter] = useState('all')
  const [selectedWork, setSelectedWork] = useState(null)
  const theme = getTheme(isLight, config.profile?.fontColor)

  const works = config.works || []

  // 平台筛选选项
  const platforms = [
    { key: 'all', name: '全部', icon: '✨' },
    { key: 'douyin', name: '抖音', icon: platformIcons.douyin },
    { key: 'xiaohongshu', name: '小红书', icon: platformIcons.xiaohongshu },
    { key: 'bilibili', name: 'B站', icon: platformIcons.bilibili },
    { key: 'kuaishou', name: '快手', icon: platformIcons.kuaishou },
    { key: 'xigua', name: '西瓜', icon: platformIcons.xigua }
  ]

  // 筛选作品
  const filteredWorks = filter === 'all'
    ? works
    : works.filter(work => work.platform === filter)

  // 格式化数字
  const formatNumber = (num) => {
    if (typeof num === 'string') return num
    if (num >= 10000) return (num / 10000).toFixed(1) + 'w'
    return num.toString()
  }

  // 打开作品链接
  const openWorkUrl = (url) => {
    if (url && url !== '#') {
      window.open(url, '_blank')
    }
  }

  return (
    <section id="works" className="py-20" style={{ backgroundColor: config.profile?.sectionBackground || '#000000' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 标题 */}
        <div className="text-center mb-12">
          <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${theme.text}`}>
            精选作品
          </h2>
          <p className={`max-w-2xl mx-auto ${theme.textSecondary}`}>
            累计创作 {config.stats?.totalWorks || 0}+ 条内容，覆盖生活方式、美食、旅行等多个领域
          </p>
        </div>

        {/* 平台筛选 */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {platforms.map((platform) => (
            <button
              key={platform.key}
              onClick={() => setFilter(platform.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === platform.key
                  ? 'bg-accent text-white'
                  : `${theme.chipBg} ${theme.chipText} hover:bg-gray-700`
              }`}
            >
              <span className="mr-1">{platform.icon}</span>
              {platform.name}
            </button>
          ))}
        </div>

        {/* 作品网格 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorks.map((work) => {
            const platformColor = platformColors[work.platform] || '#6b7280'
            const platformName = platformNames[work.platform] || work.platform
            const hasUrl = work.url && work.url !== '#'

            return (
              <div
                key={work.id}
                className={`group relative rounded-xl overflow-hidden border transition-all hover:scale-[1.02] ${theme.cardBg} ${theme.cardBorder} hover:border-gray-700`}
              >
                {/* 封面图 - 点击打开详情弹窗 */}
                <div
                  className="relative aspect-[3/4] overflow-hidden cursor-pointer"
                  onClick={() => setSelectedWork(work)}
                >
                  <img
                    src={work.cover}
                    alt={work.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* 播放按钮 */}
                  {hasUrl && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Play className="w-8 h-8 text-white fill-white" />
                      </div>
                    </div>
                  )}
                  {/* 平台标签 */}
                  <div
                    className="absolute top-3 left-3 px-2 py-1 rounded-md text-xs font-medium text-white"
                    style={{ backgroundColor: platformColor }}
                  >
                    {platformIcons[work.platform]} {platformName}
                  </div>
                  {/* 数据标签 */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white text-sm">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Play className="w-4 h-4" />
                        {formatNumber(work.views)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4" />
                        {formatNumber(work.likes)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 内容信息 */}
                <div className="p-4">
                  <h3 className={`font-semibold mb-2 line-clamp-2 group-hover:text-accent transition-colors ${theme.text}`}>
                    {work.title}
                  </h3>
                  <p className={`text-sm line-clamp-2 mb-3 ${theme.textSecondary}`}>
                    {work.description}
                  </p>
                  <div className={`flex items-center justify-between text-xs ${theme.textMuted}`}>
                    <span>{work.publishDate}</span>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        {formatNumber(work.comments)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Share2 className="w-3 h-3" />
                        {formatNumber(work.shares)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* 查看更多 - 跳转到抖音主页 */}
        <div className="text-center mt-10">
            <a
              href={config.social?.douyin || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 px-6 py-3 border rounded-full transition-all ${theme.divider} ${theme.textSecondary} hover:border-accent hover:text-accent`}
          >
            <span>查看全部作品</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
        {/* 作品详情弹窗 */}
        <WorkDetailModal
          work={selectedWork}
          isOpen={!!selectedWork}
          onClose={() => setSelectedWork(null)}
          config={config}
        />
      </div>
    </section>
  )
}

export default Works
